import { Box, TextField, Typography, Checkbox, FormControlLabel } from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Resolver } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import Decimal from 'decimal.js';

import { Button } from 'src/components/button';
import { usePostProcessMicroPayment } from 'src/lib/hooks/use-post-process-micro-payment';
import { ProcessMicroPaymentDto } from 'src/api/sagepay/dto/ProcessMicroPayment.dto';
import { PaymentOptionBanner } from 'src/components/payment-option-banner';

import { styles } from './styles';
import { MakeAPaymentFormValues } from './types';
import { makeAPaymentSchema } from './make-a-payment-schema';
import { makeAPaymentDataKeys, SpanishBuildings } from './constants';

export const MakeAPaymentPage: FC = () => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const [checkboxVal, setCheckboxVal] = useState(false);

    const building = searchParams.get('location');
    const isSpanishBuilding =
        building === SpanishBuildings.PEDRALBES || building === SpanishBuildings.POBLENOU;
    const currencySymbol = isSpanishBuilding ? '€' : '£';

    const { mutate: postProcessMicroPayment, data: microPaymentData } =
        usePostProcessMicroPayment();

    const defaultValues = {
        firstName: searchParams.get('firstName') || '',
        lastName: searchParams.get('lastName') || '',
        email: searchParams.get('email') || '',
        location: searchParams.get('location') || '',
        room: searchParams.get('room') || '',
        academicYear: searchParams.get('academicYear') || '',
        amount: searchParams.get('amount') || '',
        vitaRef: searchParams.get('vitaRef') || '',
    };

    const {
        register,
        handleSubmit,
        formState: { isValid },
    } = useForm<MakeAPaymentFormValues>({
        resolver: yupResolver(makeAPaymentSchema) as Resolver<MakeAPaymentFormValues>,
        defaultValues,
        mode: 'all',
    });

    const submitForm = handleSubmit((data) => {
        const payload = new ProcessMicroPaymentDto(
            data.firstName,
            data.lastName,
            data.email,
            data.room,
            data.academicYear,
            data.vitaRef,
            new Decimal(data.amount)
        );

        postProcessMicroPayment(payload);
    });

    const onChecked = () => {
        setCheckboxVal(!checkboxVal);
    };

    if (microPaymentData) {
        window.location.href = microPaymentData.redirect_url;
    }

    return (
        <Box css={styles.container}>
            <Box css={styles.header}>
                <Typography
                    variant="h3"
                    css={styles.title}
                    dangerouslySetInnerHTML={{
                        __html: t(
                            isValid
                                ? 'make_a_payment_page_header'
                                : 'make_a_payment_page_error_header'
                        ),
                    }}
                />
                <Typography
                    variant="body3"
                    dangerouslySetInnerHTML={{
                        __html: t(
                            isValid ? 'make_a_payment_page_body' : 'make_a_payment_page_error_body'
                        ),
                    }}
                />
            </Box>
            {isValid && (
                <Box css={styles.form} gap={4}>
                    <Box css={styles.names} gap={4}>
                        <TextField
                            label={t('make_a_payment_page_first_name_field_label')}
                            css={styles.formField}
                            variant="standard"
                            fullWidth
                            disabled
                            {...register(makeAPaymentDataKeys.firstName)}
                        />
                        <TextField
                            label={t('make_a_payment_page_last_name_field_label')}
                            css={styles.formField}
                            variant="standard"
                            fullWidth
                            disabled
                            {...register(makeAPaymentDataKeys.lastName)}
                        />
                    </Box>
                    <TextField
                        label={t('make_a_payment_page_email_field_label')}
                        css={styles.formField}
                        variant="standard"
                        fullWidth
                        disabled
                        {...register(makeAPaymentDataKeys.email)}
                    />
                    <TextField
                        label={t('make_a_payment_page_location_field_label')}
                        css={styles.formField}
                        variant="standard"
                        fullWidth
                        disabled
                        {...register(makeAPaymentDataKeys.location)}
                    />
                    <Box css={styles.room} gap={4}>
                        <TextField
                            label={t('make_a_payment_page_room_field_label')}
                            css={styles.formField}
                            variant="standard"
                            fullWidth
                            disabled
                            {...register(makeAPaymentDataKeys.room)}
                        />
                        <TextField
                            label={t('make_a_payment_page_academic_year_field_label')}
                            css={styles.formField}
                            variant="standard"
                            fullWidth
                            disabled
                            {...register(makeAPaymentDataKeys.academicYear)}
                        />
                    </Box>
                    <Box css={styles.amount} gap={4}>
                        <TextField
                            label={t('make_a_payment_page_amount_field_label')}
                            css={styles.formField}
                            variant="standard"
                            fullWidth
                            disabled
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        {currencySymbol}
                                    </InputAdornment>
                                ),
                            }}
                            {...register(makeAPaymentDataKeys.amount)}
                        />
                        <TextField
                            label={t('make_a_payment_page_vita_ref_field_label')}
                            css={styles.formField}
                            variant="standard"
                            fullWidth
                            disabled
                            {...register(makeAPaymentDataKeys.vitaRef)}
                        />
                    </Box>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={checkboxVal}
                                onChange={onChecked}
                                css={styles.checkbox}
                            />
                        }
                        label={t('make_a_payment_page_checkbox_field_label')}
                    />
                    <PaymentOptionBanner showPaymentOptions />
                    <Button
                        variant="contained"
                        onClick={submitForm}
                        disabled={!checkboxVal || !isValid}
                        css={styles.btn}
                    >
                        Pay now
                    </Button>
                </Box>
            )}
        </Box>
    );
};
